import {
  Box,
  Divider,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { debounce } from "lodash";
import { ColorPicker } from "mui-color";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

const PathwayConfig = ({ data, patchData }) => {
  const { t } = useTranslation();
  const [pathwayColor, setPathwayColorPicker] = React.useState(
    data?.data?.pathway?.color
  );
  const [pathwayDirectionColor, setPathwayDirectionColorPicker] =
    React.useState(data?.data?.pathway?.directionColor);

  const handlePathway = React.useCallback(
    (key, val) => {
      const pathwayData = { ...(data?.data?.pathway ?? {}), [key]: val };
      patchData("pathway", pathwayData);
    },
    [data?.data?.pathway, patchData]
  );

  const debouncePathwayColor = React.useCallback(
    debounce((e) => handlePathway("color", e), 300),
    [handlePathway]
  );

  const debouncePathwayDirectionColor = React.useCallback(
    debounce((e) => handlePathway("directionColor", e), 300),
    [handlePathway]
  );

  const handlePathwayType = React.useCallback(
    (type) => {
      handlePathway("type", type);
    },
    [handlePathway]
  );

  const isPathwayActive = useMemo(() => {
    return data?.data?.pathway?.active ?? false;
  }, [data?.data?.pathway?.active]);

  return (
    <>
      <Box mt={2}>
        <InputLabel shrink>{t("/leafleteditor.gui.pathwayType")}</InputLabel>
        <Select
          size="small"
          value={data?.data?.pathway?.type ?? "foot"}
          onChange={(e) => handlePathwayType(e.target.value)}
        >
          <MenuItem value="car">
            <Stack direction="row" spacing={1} alignItems="center">
              <Icon>directions_car</Icon>
              <Typography>
                {t("/leafleteditor.gui.pathwayModes.car")}
              </Typography>
            </Stack>
          </MenuItem>
          <MenuItem value="bike">
            <Stack direction="row" spacing={1} alignItems="center">
              <Icon>directions_bike</Icon>
              <Typography>
                {t("/leafleteditor.gui.pathwayModes.bike")}
              </Typography>
            </Stack>
          </MenuItem>
          <MenuItem value="foot">
            <Stack direction="row" spacing={1} alignItems="center">
              <Icon>directions_walk</Icon>
              <Typography>
                {t("/leafleteditor.gui.pathwayModes.foot")}
              </Typography>
            </Stack>
          </MenuItem>
        </Select>
      </Box>
      <Box my={2} id="itinéraire">
        <InputLabel shrink>{t("/leafleteditor.gui.pathway")}</InputLabel>
        <Stack spacing={2} direction="row" alignItems={"center"}>
          <Switch
            color="primary"
            checked={data?.data?.pathway?.active ?? false}
            onChange={(e) => {
              handlePathway("active", e.target.checked);
            }}
          />
          <Typography variant="button">
            {data?.data?.pathway?.active ? t("global.yes") : t("global.no")}
          </Typography>
        </Stack>
      </Box>
      {isPathwayActive ? (
        <>
          <Box my={2} id="pathway-color">
            <InputLabel shrink>
              {t("/leafleteditor.gui.pathwayColor")}
            </InputLabel>
            <Stack spacing={2} direction="row" alignItems={"center"}>
              <ColorPicker
                value={pathwayColor}
                onChange={(e) => {
                  setPathwayColorPicker(`#${e?.hex}`);
                  !e.error && debouncePathwayColor(e.css.backgroundColor);
                }}
                hideTextfield
                disableAlpha={data?.marker?.type === "3D"}
              />
            </Stack>
          </Box>
          <Stack direction="column" spacing={1} my={2} id="pathway-direction">
            <Stack direction="column" alignItems={"start"}>
              <InputLabel shrink>
                {t("/leafleteditor.gui.showPathwayDirection")}
              </InputLabel>
              <Stack spacing={2} direction="row" alignItems={"center"}>
                <Switch
                  color="primary"
                  checked={data?.data?.pathway?.showDirection ?? true}
                  onChange={(e) => {
                    handlePathway("showDirection", e.target.checked);
                  }}
                />
                <Typography variant="button">
                  {data?.data?.pathway?.showDirection ?? true
                    ? t("global.yes")
                    : t("global.no")}
                </Typography>
              </Stack>
            </Stack>
            {data?.data?.pathway?.showDirection && (
              <Stack direction="column" alignItems={"start"}>
                <InputLabel shrink>
                  {t("/leafleteditor.gui.pathwayDirectionColor")}
              </InputLabel>
              <ColorPicker
                value={pathwayDirectionColor}
                onChange={(e) => {
                  setPathwayDirectionColorPicker(`#${e?.hex}`);
                  !e.error &&
                    debouncePathwayDirectionColor(e.css.backgroundColor);
                }}
                hideTextfield
                disableAlpha={data?.marker?.type === "3D"}
                />
              </Stack>
            )}
          </Stack>
        </>
      ) : null}
      <Divider orientation="horizontal" flexItem />
    </>
  );
};

export default PathwayConfig;
